import {
  BooleanInput,
  CheckboxGroupInput,
  Create,
  CreateContextProvider,
  DateTimeInput,
  Form,
  required,
  SaveButton,
  SelectInput,
  TextInput,
  Toolbar,
  TopToolbar,
  useCreateController,
  useNotify,
  useRecordContext,
  useRedirect,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Card,
  Divider,
  Stack,
  Typography,
  Button as MUIButton,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Resources } from '../../resource';

import CustomTagInput from 'src/pages/admin/components/CustomTagInput';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import useTokenMetadata from './useTokenMetadata';
import { chains } from 'src/assets/data';

interface FormProps {
  isCreate: boolean;
}
const TokenMetadataForm = (props: FormProps) => {
  const { isCreate } = props;
  const t = useTranslate();
  const redirect = useRedirect();
  const { watch, setValue, trigger } = useFormContext();
  const record = useRecordContext();
  const metadata = watch('metadata') || record?.metadata || {};
  const { findToken } = useTokenMetadata();
  const [addressMessage, setAddressMessage] = useState('');
  const chainId = useWatch({ name: 'chainId' });
  const address = useWatch({ name: 'address' });
  const symbol = useWatch({ name: 'symbol' });
  const name = useWatch({ name: 'name' });
  const [noCreate, setNoCreate] = useState<boolean>(true);

  useEffect(() => {
    isCreate && handleOnBlurAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId]);

  const handleOnBlurAddress = async () => {
    setAddressMessage('');
    if (!address?.trim() || chainId === '' || chainId === undefined) {
      setValue('symbol', '');
      setValue('name', '');
      return;
    }

    try {
      const token = await findToken(address, chainId);
      if (!token) {
        return;
      }
      const metadataIsEmpty = !token.metadata || Object.keys(token.metadata).length === 0;
      if (!metadataIsEmpty) {
        setAddressMessage(t('module.tokenMetadata.validate.conflict'));
        return;
      }
      setValue('symbol', token?.symbol);
      setValue('name', token?.name);
      setNoCreate(true);
    } catch (error: any) {
      setValue('symbol', '');
      setValue('name', '');
      setAddressMessage(t('module.tokenMetadata.validate.addressNotExists'));
    }
  };

  const hasTypedRef = useRef(false);

  return (
    <>
      <Stack spacing={3} gap={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '30%', flexShrink: 0 }}>
            {t('module.tokenMetadata.address')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              source="address"
              label=""
              placeholder={t('module.tokenMetadata.address')}
              validate={[
                required(),
                () => {
                  if (addressMessage !== '') {
                    return addressMessage;
                  }
                  return undefined;
                },
              ]}
              disabled={!isCreate}
              fullWidth
              helperText={<span style={{ color: 'red' }}>{addressMessage}</span>}
              onKeyDown={(e) => {
                if (e.key === ' ') {
                  e.preventDefault();
                }
              }}
              onChange={() => {
                hasTypedRef.current = true;
              }}
              onBlur={(e) => {
                if (isCreate && hasTypedRef.current) {
                  handleOnBlurAddress();
                  hasTypedRef.current = false;
                }
              }}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '30%', flexShrink: 0 }}>
            {t('module.tokenMetadata.chain')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <SelectInput
              label={false}
              disabled={!isCreate}
              source="chainId"
              defaultValue={501424}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              choices={chains}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '30%', flexShrink: 0 }}>
            {t('module.tokenMetadata.symbol')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <Typography fontWeight={700}>
              {symbol} {symbol && name ? `(${name})` : ''}
            </Typography>
          </Box>
        </Stack>

        {/* Metadata Section */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body1" fontWeight="bold" mr={1}>
              Metadata
            </Typography>
            <Tooltip title={t('module.tokenMetadata.info')}>
              <IconButton size="small">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Stack direction={'row'} alignItems={'center'} spacing={2} mb={1}>
            <Typography variant="body2" sx={{ flexBasis: '30%', flexShrink: 0 }}>
              Official
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <BooleanInput
                source="metadata.isOfficial"
                label=""
                helperText={<span style={{ color: 'red' }}></span>}
              />
            </Box>
          </Stack>
          <Stack direction={'row'} alignItems={'center'} spacing={2} mb={1}>
            <Typography variant="body2" sx={{ flexBasis: '30%', flexShrink: 0 }}>
              KOL
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <BooleanInput
                source="metadata.isKOL"
                label=""
                helperText={<span style={{ color: 'red' }}></span>}
              />
            </Box>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            mb={1}
            mt={2}
          >
            <Typography variant="body2" sx={{ flexBasis: '30%', flexShrink: 0, mt: -3 }}>
              KOL Follower
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <TextInput
                source="metadata.kolFollower"
                label=""
                placeholder="KOL Follower"
                fullWidth
                type="number"
                helperText={<span style={{ color: 'red' }}></span>}
              />
            </Box>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            mb={1}
            mt={2}
          >
            <Box sx={{ flexBasis: '30%', flexShrink: 0, mt: -1 }}>
              <Typography variant="body2">Abandoned</Typography>
              <Typography variant="caption" color="text.secondary">
                Abandoned (community-run) at which time?
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <DateTimeInput
                source="metadata.abandoned"
                label=""
                fullWidth
                helperText={<span style={{ color: 'red' }}></span>}
              />
            </Box>
          </Stack>
        </Box>
      </Stack>
    </>
  );
};

export default TokenMetadataForm;
